import { get, post, put, del } from './apiClient';

// Type definitions
export interface Server {
  Host: string;
  Port: number;
}

export interface Cluster {
    _id: string;
    name: string;
    clusterId: string;
    Servers: Server[];
    UserName: string;
    UseTls: boolean;
    Scheme: string;
    AuthenticationMechanism: string;
    AuthenticationSource: string;
    SecretKey: string;
    SecretStoreName: string;
    status: 'active' | 'maintenance' | 'deprecated' | 'deleted';
    createdAt: string;
    updatedAt: string;
}

export interface ClusterCreate {
  clusterId: string;
  Servers: Server[];
  UserName: string;
  UseTls: boolean;
  Scheme: string;
  AuthenticationMechanism: string;
  AuthenticationSource: string;
  SecretKey: string;
  SecretStoreName: string;
}

export interface ClusterUpdate {
  clusterId?: string;
  Servers?: Server[];
  UserName?: string;
  UseTls?: boolean;
  Scheme?: string;
  AuthenticationMechanism?: string;
  AuthenticationSource?: string;
  SecretKey?: string;
  SecretStoreName?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * Get all clusters
 * @returns Promise with clusters array
 */
export const getAllClusters = (): Promise<ApiResponse<Cluster[]>> => {
  return get<ApiResponse<Cluster[]>>('/clusters');
};

/**
 * Get cluster by ID
 * @param id Cluster ID
 * @returns Promise with cluster data
 */
export const getClusterById = (id: string): Promise<ApiResponse<Cluster>> => {
  return get<ApiResponse<Cluster>>(`/clusters/${id}`);
};

/**
 * Create a new cluster
 * @param clusterData Cluster data
 * @returns Promise with created cluster
 */
export const createCluster = (clusterData: ClusterCreate): Promise<ApiResponse<Cluster>> => {
  return post<ApiResponse<Cluster>>('/clusters', clusterData);
};

/**
 * Update a cluster
 * @param id Cluster ID
 * @param updateData Data to update
 * @returns Promise with updated cluster
 */
export const updateCluster = (id: string, updateData: ClusterUpdate): Promise<ApiResponse<Cluster>> => {
  return put<ApiResponse<Cluster>>(`/clusters/${id}`, updateData);
};

/**
 * Delete a cluster (soft delete)
 * @param id Cluster ID
 * @returns Promise with deletion result
 */
export const deleteCluster = (id: string): Promise<ApiResponse<{ message: string }>> => {
  return del<ApiResponse<{ message: string }>>(`/clusters/${id}`);
};

/**
 * Get all deleted clusters
 * @returns Promise with deleted clusters array
 */
export const getDeletedClusters = (): Promise<ApiResponse<Cluster[]>> => {
  return get<ApiResponse<Cluster[]>>('/clusters/deleted/all');
};

/**
 * Restore a deleted cluster
 * @param id Cluster ID
 * @returns Promise with restored cluster
 */
export const restoreCluster = (id: string): Promise<ApiResponse<Cluster>> => {
  return post<ApiResponse<Cluster>>(`/clusters/deleted/${id}/restore`, {});
};

/**
 * Permanently delete a cluster
 * @param id Cluster ID
 * @returns Promise with deletion result
 */
export const permanentlyDeleteCluster = (id: string): Promise<ApiResponse<{ message: string }>> => {
  return del<ApiResponse<{ message: string }>>(`/clusters/deleted/${id}`);
};

/**
 * Duplicate a cluster
 * @param id Cluster ID to duplicate
 * @param newData Override data for the new cluster
 * @returns Promise with new cluster
 */
export const duplicateCluster = (id: string, newData: Partial<ClusterCreate>): Promise<ApiResponse<Cluster>> => {
  return post<ApiResponse<Cluster>>(`/clusters/${id}/duplicate`, newData);
};

/**
 * Get tenants using a cluster
 * @param clusterId Cluster ID
 * @returns Promise with tenants array
 */
export const getTenantsByCluster = (clusterId: string): Promise<ApiResponse<any[]>> => {
  return get<ApiResponse<any[]>>(`/clusters/${clusterId}/tenants`);
};